import React, { useContext } from 'react';
import styled from 'styled-components';
import { Icon } from 'components';
import FormContext, { FormContextType } from '../FormContext';
import { StyledButton } from './StyledComponents';
import ForwardRep, { RepType } from './ForwardRep';
import { ForwardRepDirty } from '../types';

const defaultDirtyState: ForwardRepDirty = {
  forwardRepsStart: false,
  forwardRepsExpiry: false,
  id: false,
};

const defaultValidityState: FormContextType['frontDeskValidState']['forwardReps'][number] =
  {
    id: false,
    isStartBeforeExpiry: false,
  };

const DEFAULT_REPS: RepType[] = [{ id: undefined }];

const ForwardMessages = () => {
  const {
    userState: user,
    setUserState,
    userStateDirty,
    setUserStateDirty,
    frontDeskValidState,
    setShowForwardReps,
  } = useContext(FormContext);
  const reps = user.frontEndInstructions?.forwardReps || DEFAULT_REPS;

  const handleRepSelect = (rep: RepType, index: number) => {
    setUserState(prev => {
      const frontEndInstructions = prev.frontEndInstructions || {
        forwardReps: DEFAULT_REPS,
      };
      const copyOfReps = [
        ...(frontEndInstructions.forwardReps || DEFAULT_REPS),
      ];
      copyOfReps[index] = rep;

      return {
        ...prev,
        frontEndInstructions: {
          ...prev.frontEndInstructions,
          forwardReps: copyOfReps,
        },
      };
    });
  };

  const removeRep = () => {
    setUserState(prev => {
      const frontEndInstructions = prev.frontEndInstructions || {
        forwardReps: [],
      };
      const copyOfReps = [
        ...(frontEndInstructions.forwardReps || DEFAULT_REPS),
      ];
      copyOfReps.pop();

      if (copyOfReps.length === 0) {
        setShowForwardReps(false);
      }

      return {
        ...prev,
        frontEndInstructions: {
          ...prev.frontEndInstructions,
          forwardReps: copyOfReps,
        },
      };
    });
  };
  const selectedRepIds = reps.map(rep => rep.id).filter(Boolean);

  return (
    <ForwardContainer>
      {reps.map((rep, i) => (
        <ForwardRep
          key={rep.id || i}
          isLast={i === reps.length - 1}
          onRepSelect={selectedRep => handleRepSelect(selectedRep, i)}
          selectedRep={rep}
          onRemoveRep={removeRep}
          selectedRepIds={selectedRepIds}
          dirtyState={
            userStateDirty.frontEndInstructions.forwardReps?.[i] ||
            defaultDirtyState
          }
          validityState={
            frontDeskValidState.forwardReps?.[i] || defaultValidityState
          }
          setDirtyState={(field: keyof ForwardRepDirty) =>
            setUserStateDirty(cur => {
              const updatedForwardReps = cur.frontEndInstructions.forwardReps;
              updatedForwardReps[i] = {
                ...cur.frontEndInstructions.forwardReps[i],
                [field]: true,
              };
              return {
                ...cur,
                frontEndInstructions: {
                  ...cur.frontEndInstructions,
                  forwardReps: updatedForwardReps,
                },
              };
            })
          }
        />
      ))}
      {/* Only allow a maximum of three forward reps */}
      {reps.length < 3 && (
        <StyledButton
          type="link"
          icon={
            <Icon type="antdPlusCircleOutlined" color="primary" width={14} />
          }
          onClick={() => handleRepSelect({ id: undefined }, reps.length)}
        >
          Add Forward
        </StyledButton>
      )}
    </ForwardContainer>
  );
};

const ForwardContainer = styled.div`
  margin-top: 20px;
`;

export default React.memo(ForwardMessages);
